import { buildEntityPath, normalizeProjectPath, splitPath } from "./build.js";
function parsePositiveIntegerSuffix(value) {
    if (!/^\d+$/.test(value)) {
        return null;
    }
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : null;
}
export function canonicalizeEntityVersionIntent(intent) {
    if (!intent) {
        return undefined;
    }
    if (intent.kind !== "diff") {
        return intent;
    }
    const fromVersionNumber = Math.min(intent.fromVersionNumber, intent.toVersionNumber);
    const toVersionNumber = Math.max(intent.fromVersionNumber, intent.toVersionNumber);
    return {
        kind: "diff",
        fromVersionNumber,
        toVersionNumber,
    };
}
function buildEntityPathFromPathOrIntent(entityPathOrIntent) {
    return typeof entityPathOrIntent === "string"
        ? stripEntityVersionPathSuffix(entityPathOrIntent)
        : buildEntityPath(entityPathOrIntent);
}
export function buildEntityVersionPreviewPath(entityPathOrIntent, versionNumber) {
    return normalizeProjectPath(`${buildEntityPathFromPathOrIntent(entityPathOrIntent)}/ver/${versionNumber}`);
}
export function buildEntityVersionDiffPath(entityPathOrIntent, fromVersionNumber, toVersionNumber) {
    const lowVersionNumber = Math.min(fromVersionNumber, toVersionNumber);
    const highVersionNumber = Math.max(fromVersionNumber, toVersionNumber);
    return normalizeProjectPath(`${buildEntityPathFromPathOrIntent(entityPathOrIntent)}/diff/${lowVersionNumber}-${highVersionNumber}`);
}
export function composeEntityVersionPath(entityPathOrIntent, intent) {
    const canonicalIntent = canonicalizeEntityVersionIntent(intent);
    if (!canonicalIntent || canonicalIntent.kind === "current") {
        return buildEntityPathFromPathOrIntent(entityPathOrIntent);
    }
    if (canonicalIntent.kind === "preview") {
        return buildEntityVersionPreviewPath(entityPathOrIntent, canonicalIntent.versionNumber);
    }
    return buildEntityVersionDiffPath(entityPathOrIntent, canonicalIntent.fromVersionNumber, canonicalIntent.toVersionNumber);
}
export function stripEntityVersionPathSuffix(path) {
    const rawSegments = splitPath(path);
    const decodedSegments = rawSegments.map((segment) => decodeURIComponent(segment));
    const suffix = parseEntityVersionSuffix(decodedSegments);
    if (!suffix) {
        return normalizeProjectPath(path);
    }
    return normalizeProjectPath(`/${rawSegments.slice(0, suffix.baseSegmentCount).join("/")}`);
}
export function parseEntityVersionSuffix(segments) {
    if (segments.length < 3) {
        return null;
    }
    const modeSegment = segments[segments.length - 2];
    const valueSegment = segments[segments.length - 1];
    if (modeSegment === "ver") {
        const versionNumber = parsePositiveIntegerSuffix(valueSegment ?? "");
        if (versionNumber === null) {
            return null;
        }
        return {
            baseSegmentCount: segments.length - 2,
            intent: { kind: "preview", versionNumber },
        };
    }
    if (modeSegment === "diff") {
        const [fromRaw, toRaw, extra] = (valueSegment ?? "").split("-");
        if (extra !== undefined || fromRaw === undefined || toRaw === undefined) {
            return null;
        }
        const fromVersionNumber = parsePositiveIntegerSuffix(fromRaw);
        const toVersionNumber = parsePositiveIntegerSuffix(toRaw);
        if (fromVersionNumber === null || toVersionNumber === null) {
            return null;
        }
        return {
            baseSegmentCount: segments.length - 2,
            intent: canonicalizeEntityVersionIntent({
                kind: "diff",
                fromVersionNumber,
                toVersionNumber,
            }),
        };
    }
    return null;
}
